import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/navbar';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import { useUnpublishedChanges } from '../context/UnpublishedChangesContext';
import dataService from '../services/dataService';
import apiClient from '../services/apiClient';
import taskContextData from '../data/TaskContext.json';

// ── Date helpers ──────────────────────────────────────────────────────────────

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isSameMonth = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const addMonths = (date, n) => { const d = new Date(date); d.setMonth(d.getMonth() + n); return d; };

const formatMonthYear = (d) => d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const formatWeekRange = (date) => {
  const s = startOfWeek(date), e = addDays(s, 6);
  if (s.getMonth() === e.getMonth())
    return `${s.toLocaleDateString('en-US', { month: 'long' })} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
  return `${s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const formatDayFull = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

const getMonthGrid = (year, month) => {
  const start = new Date(year, month, 1 - new Date(year, month, 1).getDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};

const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const toDateLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};


// ── Google Calendar-style property color palette ───────────────────────────────
const PROPERTY_COLORS = [
  '#039be5', // Peacock
  '#e67c73', // Flamingo
  '#33b679', // Sage
  '#f6bf26', // Banana
  '#8e24aa', // Grape
  '#f4511e', // Tangerine
  '#0b8043', // Basil
  '#3f51b5', // Blueberry
  '#7986cb', // Lavender
  '#d50000', // Tomato
  '#795548', // Cocoa
  '#616161', // Graphite
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MINI_DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// ── Component ─────────────────────────────────────────────────────────────────

const CalendarPage = ({ onProfileClick, onBillingClick, onSeatsUpdate }) => {
  const { user, isImpersonating, impersonatedUser, isAdmin, canManageUsersTeams } = useAuth();
  const navigate = useNavigate();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [miniDate, setMiniDate] = useState(new Date());

  // Task data
  const [tasksData, setTasksData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Property calendar toggles: set of hidden property addresses
  const [hiddenProperties, setHiddenProperties] = useState(new Set());

  // Edit modal state
  const [editingTask, setEditingTask] = useState(null);
  const editingTaskKeyRef = useRef(null);
  const [editTaskExpandedItems, setEditTaskExpandedItems] = useState(new Set());
  const [publishingTitle, setPublishingTitle] = useState(null);
  const [publishModalRecord, setPublishModalRecord] = useState(null); // { record, eligibleUsers }
  const [publishSelectedEmails, setPublishSelectedEmails] = useState(new Set());
  const [selfCalendarStatus, setSelfCalendarStatus] = useState(null);
  const [ownedConnections, setOwnedConnections] = useState([]);
  // Shared unpublished-changes tracking (synced across Tasks / Calendar / CalendarConnection)
  const { unpublishedChanges, setUnpublishedChanges, unpublishedTaskIds, setUnpublishedTaskIds } = useUnpublishedChanges();
  const unpublishedChangesRef = useRef({});
  const unpublishedInitRef = useRef(false);
  const [flashingTitles, setFlashingTitles] = useState(new Set());
  const unpublishedCardRefs = useRef({});
  // URL the user tried to navigate to while there were unpublished changes
  const [pendingNavUrl, setPendingNavUrl] = useState(null);
  const deletedTitlesRef = useRef(new Set());
  const archivedTitlesRef = useRef(new Set());
  const editingTaskRef = useRef(null);
  const timeGridRef = useRef(null);

  // Calendar list management
  const [showCreateCalModal, setShowCreateCalModal] = useState(false);
  const [createCalTitle, setCreateCalTitle] = useState('');
  const [createCalAddress, setCreateCalAddress] = useState('');
  const [isCreatingCal, setIsCreatingCal] = useState(false);
  const [createCalError, setCreateCalError] = useState('');
  const [confirmDeleteRecord, setConfirmDeleteRecord] = useState(null);
  const [deletingTitle, setDeletingTitle] = useState(null);
  const [confirmRemoveEvent, setConfirmRemoveEvent] = useState(null); // { record, taskIdx, eventTitle }

  // Archive / restore state
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'archived'
  const [confirmArchiveRecord, setConfirmArchiveRecord] = useState(null);
  const [archivingTitle, setArchivingTitle] = useState(null);
  const [restoringTitle, setRestoringTitle] = useState(null);
  const [toast, setToast] = useState(null); // { message }

  useEffect(() => { editingTaskRef.current = editingTask; }, [editingTask]);
  useEffect(() => {
    const key = editingTask
      ? `${editingTask.record?.task_list_title || ''}:${editingTask.taskIdx ?? 'new'}`
      : null;
    if (editingTaskKeyRef.current !== key) {
      editingTaskKeyRef.current = key;
      setEditTaskExpandedItems(new Set());
    }
  }, [editingTask]);
  useEffect(() => { setMiniDate(currentDate); }, [currentDate]);

  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (document.getElementById('unpublished-pop-anim')) return;
    const s = document.createElement('style');
    s.id = 'unpublished-pop-anim';
    s.textContent = '@keyframes unpublishedPop { 0%{transform:scale(1);} 40%{transform:scale(1.025);box-shadow:0 0 0 4px rgba(251,146,60,0.28),0 4px 16px rgba(251,146,60,0.18);} 100%{transform:scale(1);} }';
    document.head.appendChild(s);
  }, []);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadTasks = useCallback(async (silent = false) => {
    if (!user) return;
    if (!silent) setLoading(true);
    try {
      const accountType = user.accountType?.toLowerCase().replace(/ /g, '_') || '';
      const typesToLoad = accountType.includes('brokerage') || accountType === 'super'
        ? ['small_team', 'medium_team', 'large_team', 'solo_assistant', 'solo']
        : ['small_team', 'medium_team', 'large_team', 'solo_assistant', 'solo', 'trial'];

      const fetchConnections = () =>
        Promise.allSettled(typesToLoad.map(t => dataService.getConnections(t))).then(results =>
          results.flatMap(r => r.status === 'fulfilled' && Array.isArray(r.value) ? r.value : [])
        ).catch(() => []);

      // Invited members — query their own email directly
      if (user.role === 'member' && user.email) {
        const memberEmail = user.email.trim().toLowerCase();
        const memberResult = await dataService.getTasksByMembers([memberEmail]);
        applyTasks(
          (memberResult?.tasks ?? [])
            .filter(r => !deletedTitlesRef.current.has(r.task_list_title))
            .map(r => archivedTitlesRef.current.has(r.task_list_title) ? { ...r, archive: true } : r),
          true
        );
        return;
      }

      // Strategy 1: owner tasks endpoint + connections in parallel
      const [ownerResult, connections] = await Promise.all([
        dataService.getOwnerTasks(),
        fetchConnections(),
      ]);

      if (ownerResult !== null && ownerResult.loadStatus !== 'no_connections') {
        applyTasks(
          ownerResult.tasks
            .filter(r => !deletedTitlesRef.current.has(r.task_list_title))
            .map(r => archivedTitlesRef.current.has(r.task_list_title) ? { ...r, archive: true } : r),
          true
        );
        return;
      }

      // Strategy 2: derive email set from owned connections
      console.warn('[Calendar] /api/tasks/owner unavailable or no connections, falling back to connections + members');
      const ownerEmail = user.email?.trim().toLowerCase();
      const owned = connections.filter(c =>
        c.owner?.email?.trim().toLowerCase() === ownerEmail ||
        (c.members || []).some(m => m.email?.trim().toLowerCase() === ownerEmail && m.is_connection_owner)
      );
      const emailSet = new Set();
      if (ownerEmail) emailSet.add(ownerEmail);
      owned.forEach(c => (c.members || []).forEach(m => { if (m.email) emailSet.add(m.email.trim().toLowerCase()); }));
      if (emailSet.size === 0) { if (!silent) setTasksData([]); return; }

      const memberResult = await dataService.getTasksByMembers([...emailSet]);
      if (memberResult !== null) {
        applyTasks(
          memberResult.tasks
            .filter(r => !deletedTitlesRef.current.has(r.task_list_title))
            .map(r => archivedTitlesRef.current.has(r.task_list_title) ? { ...r, archive: true } : r),
          true
        );
      } else {
        // Strategy 3: local fallback
        console.warn('[Calendar] All API attempts failed, using local TaskContext data');
        applyTasks(taskContextData.filter(r => emailSet.has(r.id) && r.active && !deletedTitlesRef.current.has(r.task_list_title)));
      }
    } catch (err) {
      console.error('[Calendar] loadTasks error:', err);
      applyTasks(taskContextData.filter(r => r.active && !deletedTitlesRef.current.has(r.task_list_title)));
    } finally {
      setLoading(false);
    }

    function applyTasks(records, fromAPI = false) {
      if (silent) {
        // When the API confirmed success, always apply (even if empty) so remote deletions
        // are reflected without a manual refresh. Keep prev only for local-fallback data
        // where an empty result signals a network failure, not a real empty state.
        setTasksData(prev => (fromAPI || records.length > 0) ? records : prev);
      } else {
        setTasksData(records);
      }
    }
  }, [user]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  useEffect(() => {
    if (!user) return;
    let timer = null;
    const poll = () => { if (!document.hidden && !editingTaskRef.current) loadTasks(true); };
    const onVisibility = () => {
      if (document.hidden) { clearInterval(timer); timer = null; }
      else if (!timer) timer = setInterval(poll, 10_000);
    };
    timer = setInterval(poll, 10_000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisibility); };
  }, [user, loadTasks]);

  useEffect(() => {
    if ((view === 'week' || view === 'day') && timeGridRef.current) {
      const now = new Date();
      const pct = (now.getHours() * 60 + now.getMinutes()) / 1440;
      timeGridRef.current.scrollTop = Math.max(0, pct * timeGridRef.current.scrollHeight - 200);
    }
  }, [view]);

  // Initialize unpublished-changes state from backend data on first load
  useEffect(() => {
    if (loading || unpublishedInitRef.current) return;
    unpublishedInitRef.current = true;
    const effectiveEmail = (isImpersonating ? impersonatedUser?.email : user?.email)?.toLowerCase();
    const initial = {};
    const initialTaskIds = {};
    const backfillMap = {};
    tasksData.forEach(record => {
      const title = record.task_list_title;
      if (!title) return;
      // Skip records that belong to other users — their update_timestamp values are not this user's edits.
      if (effectiveEmail && record.id && record.id.toLowerCase() !== effectiveEmail) return;
      // A task is genuinely unpublished only if its update_timestamp is AFTER the last publish.
      // update_timestamp is never cleared by publish, so comparing against published_at is required.
      const publishedAt = record.published_at ? new Date(record.published_at) : null;
      const isUnpublishedChange = (t) =>
        !!t.update_timestamp && (!publishedAt || new Date(t.update_timestamp) > publishedAt);
      // Backfill task_id for legacy tasks that have genuinely new unpublished changes but no task_id.
      let tasks = record.tasks || [];
      if (tasks.some(t => isUnpublishedChange(t) && !t.task_id)) {
        tasks = tasks.map(t =>
          (isUnpublishedChange(t) && !t.task_id)
            ? { ...t, task_id: `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }
            : t
        );
        backfillMap[title] = tasks;
      }
      // Only count tasks the current user personally has pending — tasks with update_timestamp are user-specific drafts.
      // Do NOT use record.has_unpublished_changes / record.unpublished_change_count, which are shared flags
      // that reflect any team member's changes and would bleed other users' indicators into this user's view.
      const dirtyTasks = tasks.filter(t => t.task_id && isUnpublishedChange(t));
      if (dirtyTasks.length > 0) {
        initial[title] = { count: dirtyTasks.length, lastEditedAt: record.last_edited_at || null };
        initialTaskIds[title] = new Set(dirtyTasks.map(t => t.task_id));
      }
    });
    // Apply backfilled task_ids to tasksData state so cards render with their IDs.
    if (Object.keys(backfillMap).length > 0) {
      setTasksData(prev => prev.map(r => {
        const bf = backfillMap[r.task_list_title];
        return bf ? { ...r, tasks: bf } : r;
      }));
    }
    if (Object.keys(initial).length > 0) setUnpublishedChanges(prev => ({ ...prev, ...initial }));
    if (Object.keys(initialTaskIds).length > 0) {
      setUnpublishedTaskIds(prev => {
        const next = { ...prev };
        Object.entries(initialTaskIds).forEach(([title, ids]) => {
          next[title] = ids;
        });
        return next;
      });
    }
  }, [tasksData, loading, setUnpublishedChanges, setUnpublishedTaskIds, isImpersonating, impersonatedUser, user]);

  // Keep ref in sync so the click-capture handler always reads the latest value
  useEffect(() => {
    unpublishedChangesRef.current = unpublishedChanges;
  }, [unpublishedChanges]);

  // Intercept NavBar <Link> clicks before React Router handles them
  useEffect(() => {
    const handleNavClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const navbar = document.querySelector('.main-navbar');
      if (!navbar || !navbar.contains(anchor)) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href === window.location.pathname) return;
      if (Object.values(unpublishedChangesRef.current).some(v => v.count > 0)) {
        e.preventDefault();
        e.stopPropagation();
        setPendingNavUrl(href);
      }
    };
    document.addEventListener('click', handleNavClick, true);
    return () => document.removeEventListener('click', handleNavClick, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn before browser close / tab close when there are unpublished changes
  useEffect(() => {
    const handler = (e) => {
      if (Object.values(unpublishedChangesRef.current).some(v => v.count > 0)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Fetch the acting user's personal calendar auth status once on login
  useEffect(() => {
    if (!user) { setSelfCalendarStatus(null); return; }
    apiClient.get('/api/calendar/auth/status').then(res => {
      setSelfCalendarStatus({
        enabled: res.data.calendar_sync_enabled !== false,
        connected: !!res.data.connected,
        provider: res.data.provider || null,
      });
    }).catch(() => setSelfCalendarStatus({ enabled: true, connected: false, provider: null }));
  }, [user]);

  // Load owned connections once so openPublishModal can build the full member list
  useEffect(() => {
    if (!user || user.role === 'member') { setOwnedConnections([]); return; }
    const ownerEmail = user.email?.trim().toLowerCase();
    const accountType = user.accountType?.toLowerCase().replace(/ /g, '_') || '';
    const typesToLoad = accountType.includes('brokerage') || accountType === 'super'
      ? ['small_team', 'medium_team', 'large_team', 'solo_assistant', 'solo']
      : ['small_team', 'medium_team', 'large_team', 'solo_assistant', 'solo', 'trial'];
    Promise.allSettled(typesToLoad.map(t => dataService.getConnections(t))).then(results => {
      const allConns = results.flatMap(r => r.status === 'fulfilled' && Array.isArray(r.value) ? r.value : []);
      const owned = allConns.filter(c =>
        c.owner?.email?.trim().toLowerCase() === ownerEmail ||
        (c.members || []).some(m => m.email?.trim().toLowerCase() === ownerEmail && m.is_connection_owner)
      );
      setOwnedConnections(owned);
    });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const canManageCalendars = useMemo(() => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    const LEAD = new Set(['team_lead', 'solo_agent', 'single_agent', 'admin']);
    return LEAD.has(user.team_role);
  }, [user]);

  const canArchiveTaskLists = useMemo(() => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    if (isAdmin && canManageUsersTeams) return true;
    const email = user.email?.toLowerCase();
    for (const conn of ownedConnections) {
      const m = (conn.members || []).find(m => m.email?.toLowerCase() === email);
      if (m) {
        const val = m.can_archive_task_lists;
        if (val !== undefined) return Boolean(val);
        return m.role === 'team_lead';
      }
    }
    const LEAD = new Set(['team_lead', 'solo_agent', 'single_agent', 'admin']);
    return LEAD.has(user.team_role);
  }, [user, isAdmin, canManageUsersTeams, ownedConnections]);

  const canDeleteTaskLists = useMemo(() => {
    if (!user) return false;
    if (user.role === 'owner') return true;
    if (isAdmin && canManageUsersTeams) return true;
    const email = user.email?.toLowerCase();
    for (const conn of ownedConnections) {
      const m = (conn.members || []).find(m => m.email?.toLowerCase() === email);
      if (m) { const val = m.can_delete_task_lists ?? m.permissions?.can_delete_task_lists; if (val !== undefined) return Boolean(val); }
    }
    return user.team_role === 'team_lead';
  }, [user, isAdmin, canManageUsersTeams, ownedConnections]);

  const effectiveTab = canArchiveTaskLists ? activeTab : 'active';

  const visibleRecords = useMemo(() =>
    tasksData.filter(r => effectiveTab === 'active' ? !r.archive : r.archive === true),
    [tasksData, effectiveTab]
  );

  // ── Property colors — stable mapping from address → color ─────────────────
  const propertyColors = useMemo(() => {
    const addresses = [...new Set(visibleRecords.map(r => r.property_address).filter(Boolean))].sort();
    const map = {};
    addresses.forEach((addr, i) => { map[addr] = PROPERTY_COLORS[i % PROPERTY_COLORS.length]; });
    return map;
  }, [visibleRecords]);

  // ── Flatten events ────────────────────────────────────────────────────────
  const allEvents = useMemo(() =>
    visibleRecords.flatMap((record, recordIdx) =>
      (record.tasks || []).map((task, taskIdx) => ({
        ...task,
        record,
        recordIdx,
        taskIdx,
        date: (task.start || task.due) ? new Date(task.start || task.due) : null,
        endDate: task.end ? new Date(task.end) : null,
      }))
    ), [visibleRecords]
  );

  // Visible events = not hidden by property toggle
  const events = useMemo(() =>
    allEvents.filter(e => !hiddenProperties.has(e.record?.property_address)),
    [allEvents, hiddenProperties]
  );

  const eventsForDay = useCallback((day) =>
    events
      .filter(e => e.date && isSameDay(e.date, day))
      .sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0)),
    [events]
  );

  // ── Event chip styling — property color, faded if completed ───────────────
  const chipStyle = useCallback((ev) => {
    const color = propertyColors[ev.record?.property_address] || '#94a3b8';
    const done = ev.status === 'completed';
    const now = new Date();
    const overdue = !done && ev.date && ev.date < now;
    return { color, done, overdue };
  }, [propertyColors]);

  // ── Task stats for sidebar ────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = events.length;
    const completed = events.filter(e => e.status === 'completed').length;
    const inProgress = events.filter(e => e.status === 'inProgress').length;
    const now = new Date();
    const overdue = events.filter(e => e.status !== 'completed' && e.date && e.date < now).length;
    return { total, completed, inProgress, overdue };
  }, [events]);

  // ── Property toggle ───────────────────────────────────────────────────────
  const toggleProperty = (addr) => {
    setHiddenProperties(prev => {
      const next = new Set(prev);
      if (next.has(addr)) next.delete(addr); else next.add(addr);
      return next;
    });
  };

  // ── Edit / save / publish ─────────────────────────────────────────────────
  const openEdit = (ev) => {
    const inferredType = ev.item_type || (ev.start_datetime || ev.end_datetime ? 'scheduled_event' : 'deadline');
    const task = {
      title: ev.title,
      start: ev.start || ev.start_datetime || ev.due,
      end: ev.end || ev.end_datetime || null,
      due: ev.due || ev.start || ev.start_datetime,
      all_day: ev.all_day || false,
      location: ev.location || '',
      description: ev.description || '',
      status: ev.status || 'notStarted',
      action_items: ev.action_items || [],
      item_type: inferredType,
      task_id: ev.task_id,
      update_timestamp: ev.update_timestamp,
    };
    setEditingTask({ task: { ...task }, originalTask: { ...task }, record: ev.record, taskIdx: ev.taskIdx });
  };

  const handleAddEvent = (record, defaultDate = null) => {
    const base = defaultDate || new Date();
    const startISO = base.toISOString();
    const endISO = new Date(base.getTime() + 60 * 60 * 1000).toISOString();
    const blank = {
      title: '', start: startISO, end: endISO, due: startISO,
      all_day: false, location: '', description: '',
      status: 'notStarted', action_items: [],
      item_type: 'deadline',
      task_id: `tid_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      update_timestamp: startISO,
    };
    setEditingTask({ task: { ...blank }, originalTask: { ...blank }, record, taskIdx: null, isNewEvent: true });
  };

  const handleFlashUnpublished = useCallback((title) => {
    const doFlash = () => {
      const cards = Object.entries(unpublishedCardRefs.current)
        .filter(([k]) => k.startsWith(`${title}:::`))
        .map(([, el]) => el)
        .filter(Boolean);
      if (cards.length > 0) cards[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setFlashingTitles(prev => new Set([...prev, title]));
      setTimeout(() => setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; }), 800);
    };

    // Navigate to the first unpublished task's due date if it's out of the current view
    const unpubIds = unpublishedTaskIds[title];
    const record = tasksData.find(r => r.task_list_title === title);
    const firstDirtyTask = unpubIds && record
      ? (record.tasks || []).find(t => t.task_id && unpubIds.has(t.task_id) && t.due)
      : null;

    if (firstDirtyTask?.due) {
      setCurrentDate(new Date(firstDirtyTask.due));
      setTimeout(doFlash, 150);
    } else {
      doFlash();
    }
  }, [tasksData, unpublishedTaskIds]);

  const handleSave = () => {
    const { task, taskIdx, record, isNewEvent } = editingTask;
    const title = record.task_list_title;
    const isScheduled = task.item_type === 'scheduled_event';
    const saved = {
      ...task,
      due: task.start || task.due,
      action_items: (task.action_items || []).filter(s => s.trim()),
      update_timestamp: new Date().toISOString(),
      ...(isScheduled ? { start_datetime: task.start, end_datetime: task.end } : {}),
    };
    setEditingTask(null);
    setUnpublishedChanges(prev => ({
      ...prev,
      [title]: { count: (prev[title]?.count || 0) + 1, lastEditedAt: new Date().toISOString() },
    }));
    setUnpublishedTaskIds(prev => {
      const s = new Set(prev[title] || []);
      if (saved.task_id) s.add(saved.task_id);
      return { ...prev, [title]: s };
    });
    if (isNewEvent) {
      const currentRecord = tasksData.find(r => r.task_list_title === title);
      const newTasks = [...(currentRecord?.tasks || []), saved];
      setTasksData(prev => prev.map(rec =>
        rec.task_list_title !== title ? rec : { ...rec, tasks: newTasks }
      ));
      dataService.replaceTasksDraft(title, newTasks).then(ok => {
        if (!ok) console.warn('[Calendar] New event draft save may have failed');
      });
    } else {
      setTasksData(prev => prev.map(rec =>
        rec.task_list_title !== title ? rec : {
          ...rec, tasks: rec.tasks.map((t, ti) => ti === taskIdx ? { ...t, ...saved } : t)
        }
      ));
      dataService.updateTaskItemDraft(title, taskIdx, saved).then(ok => {
        if (!ok) console.warn('[Calendar] Draft save to DynamoDB may have failed');
      });
    }
  };

  const handleCreateCal = async () => {
    const trimmed = createCalTitle.trim();
    if (!trimmed || isCreatingCal) return;
    setIsCreatingCal(true);
    setCreateCalError('');
    const result = await dataService.createTaskList(trimmed, createCalAddress.trim());
    setIsCreatingCal(false);
    if (!result || result.error) {
      setCreateCalError(result?.error || 'Failed to create calendar. Please try again.');
      return;
    }
    setTasksData(prev => [result, ...prev]);
    setShowCreateCalModal(false);
    setCreateCalTitle('');
    setCreateCalAddress('');
  };

  const handleConfirmDeleteCalendar = async () => {
    const record = confirmDeleteRecord;
    setConfirmDeleteRecord(null);
    const title = record.task_list_title;
    setDeletingTitle(title);
    const ok = await dataService.deleteTaskList(record.id, title);
    setDeletingTitle(null);
    if (ok) {
      deletedTitlesRef.current.add(title);
      setTasksData(prev => prev.filter(r => !deletedTitlesRef.current.has(r.task_list_title)));
    } else console.warn('[Calendar] Delete calendar may have failed');
  };

  const showToast = (message) => {
    setToast({ message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleArchive = (record) => setConfirmArchiveRecord(record);

  const handleConfirmArchive = async () => {
    const record = confirmArchiveRecord;
    setConfirmArchiveRecord(null);
    const title = record.task_list_title;
    setArchivingTitle(title);
    const ok = await dataService.archiveTaskList(record.id, title);
    setArchivingTitle(null);
    if (ok) {
      archivedTitlesRef.current.add(title);
      setTasksData(prev => prev.map(r =>
        r.task_list_title === title ? { ...r, archive: true } : r
      ));
      showToast('Calendar archived.');
    } else console.warn('[Calendar] Archive calendar may have failed');
  };

  const handleRestore = async (record) => {
    const title = record.task_list_title;
    setRestoringTitle(title);
    const ok = await dataService.restoreTaskList(record.id, title);
    setRestoringTitle(null);
    if (ok) {
      archivedTitlesRef.current.delete(title);
      setTasksData(prev => prev.map(r =>
        r.task_list_title === title ? { ...r, archive: false } : r
      ));
      showToast('Calendar restored to Active.');
    } else console.warn('[Calendar] Restore calendar may have failed');
  };

  const handleConfirmRemoveEvent = () => {
    const { record, taskIdx } = confirmRemoveEvent;
    const title = record.task_list_title;
    const newTasks = (record.tasks || []).filter((_, ti) => ti !== taskIdx);
    setTasksData(prev => prev.map(rec =>
      rec.task_list_title !== title ? rec : { ...rec, tasks: newTasks }
    ));
    setUnpublishedChanges(prev => ({
      ...prev,
      [title]: { count: (prev[title]?.count || 0) + 1, lastEditedAt: new Date().toISOString() },
    }));
    setUnpublishedTaskIds(prev => {
      const s = new Set(prev[title] || []);
      const removedTask = (record.tasks || [])[taskIdx];
      if (removedTask?.task_id) s.delete(removedTask.task_id);
      return { ...prev, [title]: s };
    });
    dataService.replaceTasksDraft(title, newTasks).then(ok => {
      if (!ok) console.warn('[Calendar] Remove event draft save may have failed');
    });
    setConfirmRemoveEvent(null);
    setEditingTask(null);
  };

  const openPublishModal = (record) => {
    const recordEmail = record.id?.toLowerCase();
    const connection = ownedConnections.find(conn =>
      (conn.members || []).some(m => m.email?.toLowerCase() === recordEmail)
    );
    const allMembers = connection?.members?.length
      ? connection.members
      : [{ email: record.id, role: 'team_lead' }];

    const currentEmail = user?.email?.toLowerCase();
    const userRole = user?.role;
    const userTeamRole = user?.team_role;
    const teamLead = allMembers.find(m => m.role === 'team_lead') || allMembers[0];

    const isCurrentUserMember = userRole !== 'owner' && userTeamRole !== 'team_lead';

    let eligible;
    if (!isCurrentUserMember) {
      // Owner/Lead Agent: self + all other members selectable.
      const selfEntry = allMembers.find(m => m.email?.toLowerCase() === currentEmail) || { email: currentEmail };
      const otherMembers = allMembers.filter(m => m.email?.toLowerCase() !== currentEmail);
      eligible = [selfEntry, ...otherMembers];
    } else {
      // Regular member: team lead is required (locked); self is publisher.
      const selfMemberObj = allMembers.find(m => m.email?.toLowerCase() === currentEmail) || { email: currentEmail };
      const lockedLead = teamLead && teamLead.email?.toLowerCase() !== currentEmail
        ? { ...teamLead, lockedInfo: true }
        : null;
      eligible = [selfMemberObj, ...(lockedLead ? [lockedLead] : [])];
    }
    if (!eligible.length) eligible = [{ email: record.id, role: 'team_lead' }];

    // Pre-select all non-locked entries; acting user always included.
    const initialSelected = new Set(
      eligible.filter(m => !m.lockedInfo).map(m => m.email?.toLowerCase()).filter(Boolean)
    );
    setPublishSelectedEmails(initialSelected);
    setPublishModalRecord({ record, eligibleUsers: eligible, isCurrentUserMember });
  };

  const handlePublishConfirm = async () => {
    if (!publishModalRecord) return;
    const { record } = publishModalRecord;
    const title = record.task_list_title;
    const connectionId = record.connection_id || '';
    const currentEmail = user?.email?.toLowerCase();
    const currentRecord = tasksData.find(r => r.task_list_title === title);
    setPublishModalRecord(null);
    setPublishingTitle(title);
    if (currentRecord?.tasks) {
      await dataService.propagateTasks(title, currentRecord.tasks);
    }
    await dataService.publishTasks(currentEmail, title, connectionId, [...publishSelectedEmails]);
    setUnpublishedChanges(prev => { const n = { ...prev }; delete n[title]; return n; });
    setUnpublishedTaskIds(prev => { const n = { ...prev }; delete n[title]; return n; });
    setFlashingTitles(prev => { const n = new Set(prev); n.delete(title); return n; });
    setPublishingTitle(null);
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const goToday = () => setCurrentDate(new Date());
  const goPrev = () => setCurrentDate(d =>
    view === 'month' ? addMonths(d, -1) : view === 'week' ? addDays(d, -7) : addDays(d, -1)
  );
  const goNext = () => setCurrentDate(d =>
    view === 'month' ? addMonths(d, 1) : view === 'week' ? addDays(d, 7) : addDays(d, 1)
  );
  const periodLabel = view === 'month' ? formatMonthYear(currentDate)
    : view === 'week' ? formatWeekRange(currentDate)
    : formatDayFull(currentDate);

  // ── Month view ────────────────────────────────────────────────────────────
  const renderMonth = () => {
    const today = new Date();
    const days = getMonthGrid(currentDate.getFullYear(), currentDate.getMonth());
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          {DAY_LABELS.map(d => (
            <div key={d} style={{ textAlign: 'center', padding: '8px 0', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(6, 1fr)', flex: 1, minHeight: 0 }}>
          {days.map((day, i) => {
            const isToday = isSameDay(day, today);
            const inMonth = isSameMonth(day, currentDate);
            const dayEvs = eventsForDay(day);
            const overflow = dayEvs.length - 3;
            return (
              <div key={i}
                onClick={() => { setCurrentDate(day); setView('day'); }}
                onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { if (!isToday) e.currentTarget.style.background = '#fff'; }}
                style={{ border: '1px solid #f1f5f9', borderTop: '1px solid #e2e8f0', padding: '4px 6px', background: isToday ? '#eff6ff' : '#fff', cursor: 'pointer', overflow: 'hidden', minHeight: 0, transition: 'background 0.1s' }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '50%', marginBottom: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isToday ? '#1a73e8' : 'transparent', color: isToday ? '#fff' : inMonth ? '#1e293b' : '#cbd5e1', fontSize: '13px', fontWeight: isToday ? 700 : inMonth ? 500 : 400 }}>
                  {day.getDate()}
                </div>
                {dayEvs.slice(0, 3).map((ev, ei) => {
                  const { color, done, overdue } = chipStyle(ev);
                  const isUnpublished = !!ev.task_id && !!unpublishedTaskIds[ev.record?.task_list_title]?.has(ev.task_id);
                  const isFlashing = isUnpublished && flashingTitles.has(ev.record?.task_list_title);
                  return (
                    <div key={ei}
                      ref={isUnpublished && ev.task_id ? el => {
                        const k = `${ev.record.task_list_title}:::${ev.task_id}`;
                        if (el) unpublishedCardRefs.current[k] = el;
                        else delete unpublishedCardRefs.current[k];
                      } : undefined}
                      onClick={e => { e.stopPropagation(); openEdit(ev); }}
                      title={ev.title}
                      style={{ background: color, color: '#fff', borderRadius: '3px', padding: '1px 5px', fontSize: '11px', fontWeight: 500, marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', lineHeight: '1.6', opacity: done ? 0.45 : 1, outline: isUnpublished ? '2px solid #f97316' : overdue ? '1.5px dashed rgba(255,255,255,0.7)' : 'none', animation: isFlashing ? 'unpublishedPop 0.7s ease-out' : undefined }}>
                      <span style={{ opacity: 0.9, marginRight: '3px', fontSize: '10px' }}>
                        {ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </span>
                      <span style={{ textDecoration: done ? 'line-through' : 'none' }}>
                        {overdue ? '! ' : ''}{ev.title}
                      </span>
                    </div>
                  );
                })}
                {overflow > 0 && (
                  <div onClick={e => { e.stopPropagation(); setCurrentDate(day); setView('day'); }}
                    style={{ fontSize: '11px', color: '#1a73e8', fontWeight: 600, paddingLeft: '2px', cursor: 'pointer' }}>
                    +{overflow} more
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Week view ─────────────────────────────────────────────────────────────
  const renderWeek = () => {
    const today = new Date();
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const CELL_H = 48;
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(7, 1fr)', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div />
          {weekDays.map((day, i) => {
            const isToday = isSameDay(day, today);
            return (
              <div key={i} style={{ textAlign: 'center', padding: '8px 0', cursor: 'pointer' }} onClick={() => { setCurrentDate(day); setView('day'); }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{DAY_LABELS[day.getDay()]}</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', marginTop: '2px', background: isToday ? '#1a73e8' : 'transparent', color: isToday ? '#fff' : '#1e293b', fontSize: '18px', fontWeight: isToday ? 700 : 400 }}>
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>
        <div ref={timeGridRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(7, 1fr)', height: `${24 * CELL_H}px`, position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              {HOURS.map(h => (
                <div key={h} style={{ position: 'absolute', top: `${h * CELL_H}px`, right: '6px', fontSize: '10px', color: '#94a3b8', transform: 'translateY(-50%)', whiteSpace: 'nowrap', lineHeight: 1 }}>
                  {h === 0 ? '' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}
                </div>
              ))}
            </div>
            {weekDays.map((day, di) => {
              const isToday = isSameDay(day, today);
              const dayEvs = eventsForDay(day);
              return (
                <div key={di} style={{ position: 'relative', borderLeft: '1px solid #f1f5f9', background: isToday ? '#fafbff' : '#fff' }}>
                  {HOURS.map(h => <div key={h} style={{ position: 'absolute', top: `${h * CELL_H}px`, left: 0, right: 0, borderTop: '1px solid #f1f5f9' }} />)}
                  {isToday && (() => {
                    const now = new Date();
                    const pct = (now.getHours() * 60 + now.getMinutes()) / 1440 * 100;
                    return (
                      <div style={{ position: 'absolute', top: `${pct}%`, left: 0, right: 0, height: '2px', background: '#1a73e8', zIndex: 2 }}>
                        <div style={{ position: 'absolute', left: '-4px', top: '-4px', width: '8px', height: '8px', borderRadius: '50%', background: '#1a73e8' }} />
                      </div>
                    );
                  })()}
                  {dayEvs.map((ev, ei) => {
                    const { color, done, overdue } = chipStyle(ev);
                    const topPct = (ev.date.getHours() * 60 + ev.date.getMinutes()) / 1440 * 100;
                    const isUnpublished = !!ev.task_id && !!unpublishedTaskIds[ev.record?.task_list_title]?.has(ev.task_id);
                    const isFlashing = isUnpublished && flashingTitles.has(ev.record?.task_list_title);
                    return (
                      <div key={ei} onClick={() => openEdit(ev)} title={ev.title}
                        ref={isUnpublished && ev.task_id ? el => {
                          const k = `${ev.record.task_list_title}:::${ev.task_id}`;
                          if (el) unpublishedCardRefs.current[k] = el;
                          else delete unpublishedCardRefs.current[k];
                        } : undefined}
                        style={{ position: 'absolute', top: `${topPct}%`, left: `${(ei % 2) * 48}%`, width: '46%', minHeight: '18px', background: color, color: '#fff', borderRadius: '4px', padding: '2px 5px', fontSize: '11px', fontWeight: 500, cursor: 'pointer', zIndex: 1, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', lineHeight: '1.4', opacity: done ? 0.45 : 1, outline: isUnpublished ? '2px solid #f97316' : overdue ? '1.5px dashed rgba(255,255,255,0.6)' : 'none', animation: isFlashing ? 'unpublishedPop 0.7s ease-out' : undefined }}>
                        <div style={{ fontSize: '10px', opacity: 0.9 }}>{ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</div>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: done ? 'line-through' : 'none' }}>
                          {overdue ? '! ' : ''}{ev.title}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ── Day view ──────────────────────────────────────────────────────────────
  const renderDay = () => {
    const today = new Date();
    const isToday = isSameDay(currentDate, today);
    const dayEvs = eventsForDay(currentDate);
    const CELL_H = 60;
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{currentDate.toLocaleDateString('en-US', { weekday: 'long' })}</div>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', background: isToday ? '#1a73e8' : 'transparent', color: isToday ? '#fff' : '#1e293b', fontSize: '20px', fontWeight: 700 }}>
            {currentDate.getDate()}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#94a3b8' }}>{dayEvs.length} task{dayEvs.length !== 1 ? 's' : ''}</div>
        </div>
        <div ref={timeGridRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr', height: `${24 * CELL_H}px`, position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              {HOURS.map(h => (
                <div key={h} style={{ position: 'absolute', top: `${h * CELL_H}px`, right: '8px', fontSize: '11px', color: '#94a3b8', transform: 'translateY(-50%)', whiteSpace: 'nowrap', lineHeight: 1 }}>
                  {h === 0 ? '' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}
                </div>
              ))}
            </div>
            <div style={{ position: 'relative', borderLeft: '1px solid #e2e8f0' }}>
              {HOURS.map(h => <div key={h} style={{ position: 'absolute', top: `${h * CELL_H}px`, left: 0, right: 0, borderTop: '1px solid #f1f5f9' }} />)}
              {isToday && (() => {
                const now = new Date();
                const pct = (now.getHours() * 60 + now.getMinutes()) / 1440 * 100;
                return (
                  <div style={{ position: 'absolute', top: `${pct}%`, left: 0, right: 0, height: '2px', background: '#1a73e8', zIndex: 2 }}>
                    <div style={{ position: 'absolute', left: '-4px', top: '-4px', width: '8px', height: '8px', borderRadius: '50%', background: '#1a73e8' }} />
                  </div>
                );
              })()}
              {dayEvs.map((ev, ei) => {
                const { color, done, overdue } = chipStyle(ev);
                const topPct = (ev.date.getHours() * 60 + ev.date.getMinutes()) / 1440 * 100;
                const isUnpublished = !!ev.task_id && !!unpublishedTaskIds[ev.record?.task_list_title]?.has(ev.task_id);
                const isFlashing = isUnpublished && flashingTitles.has(ev.record?.task_list_title);
                return (
                  <div key={ei} onClick={() => openEdit(ev)}
                    ref={isUnpublished && ev.task_id ? el => {
                      const k = `${ev.record.task_list_title}:::${ev.task_id}`;
                      if (el) unpublishedCardRefs.current[k] = el;
                      else delete unpublishedCardRefs.current[k];
                    } : undefined}
                    style={{ position: 'absolute', top: `${topPct}%`, left: `${ei * 4}px`, right: '8px', minHeight: '48px', background: color, color: '#fff', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', zIndex: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', borderLeft: `4px solid rgba(0,0,0,0.15)`, opacity: done ? 0.5 : 1, outline: isUnpublished ? '2px solid #f97316' : overdue ? '2px dashed rgba(255,255,255,0.6)' : 'none', animation: isFlashing ? 'unpublishedPop 0.7s ease-out' : undefined }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '2px', textDecoration: done ? 'line-through' : 'none' }}>
                      {overdue ? '! ' : ''}{ev.title}
                    </div>
                    <div style={{ fontSize: '11px', opacity: 0.9 }}>
                      {ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      {' · '}{ev.record.task_list_title}
                    </div>
                    {ev.action_items?.length > 0 && (
                      <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '3px' }}>
                        {ev.action_items.length} action item{ev.action_items.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Mini calendar ─────────────────────────────────────────────────────────
  const renderMiniCal = () => {
    const today = new Date();
    const year = miniDate.getFullYear();
    const month = miniDate.getMonth();
    const days = getMonthGrid(year, month);
    return (
      <div style={{ padding: '14px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <button onClick={() => setMiniDate(d => addMonths(d, -1))} style={miniBtn}>‹</button>
          <div style={{ flex: 1, textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
            {MONTH_NAMES[month]} {year}
          </div>
          <button onClick={() => setMiniDate(d => addMonths(d, 1))} style={miniBtn}>›</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' }}>
          {MINI_DAY_LABELS.map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', fontWeight: 600, padding: '2px 0' }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {days.map((day, i) => {
            const isToday = isSameDay(day, today);
            const isSelected = isSameDay(day, currentDate);
            const inMiniMonth = isSameMonth(day, miniDate);
            const hasEvs = events.some(e => e.date && isSameDay(e.date, day));
            return (
              <div key={i} onClick={() => setCurrentDate(day)}
                style={{ textAlign: 'center', fontSize: '12px', padding: '4px 2px', cursor: 'pointer', borderRadius: '50%', position: 'relative', background: isSelected ? '#1a73e8' : isToday ? '#e8f0fe' : 'transparent', color: isSelected ? '#fff' : isToday ? '#1a73e8' : inMiniMonth ? '#334155' : '#cbd5e1', fontWeight: isToday || isSelected ? 700 : 400 }}>
                {day.getDate()}
                {hasEvs && !isSelected && (
                  <div style={{ position: 'absolute', bottom: '1px', left: '50%', transform: 'translateX(-50%)', width: '4px', height: '4px', borderRadius: '50%', background: isToday ? '#1a73e8' : '#94a3b8' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const miniBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#64748b', padding: '2px 6px', borderRadius: '4px', lineHeight: 1 };

  // ── My Properties sidebar list ────────────────────────────────────────────
  const renderPropertyList = () => {
    const properties = Object.keys(propertyColors);
    const unscheduled = events.filter(e => !e.date);
    // Show the section when there are visible records, or when on the archived tab so the
    // tab switcher and empty state remain visible even if there are no archived calendars.
    if (properties.length === 0 && unscheduled.length === 0 && !(canArchiveTaskLists && effectiveTab === 'archived')) return null;

    const now = new Date();
    const allVisible = hiddenProperties.size === 0;

    return (
      <>
        {/* No Due Date — only meaningful in the active tab */}
        {effectiveTab === 'active' && unscheduled.length > 0 && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
              No Due Date ({unscheduled.length})
            </div>
            {unscheduled.slice(0, 4).map((ev, i) => {
              const color = propertyColors[ev.record?.property_address] || '#94a3b8';
              return (
                <div key={i} onClick={() => openEdit(ev)} title={ev.title}
                  style={{ background: color, color: '#fff', borderRadius: '4px', padding: '4px 8px', marginBottom: '4px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.title}
                </div>
              );
            })}
            {unscheduled.length > 4 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>+{unscheduled.length - 4} more</div>}
          </div>
        )}

        {/* My Calendars */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0' }}>
          {/* Active / Archived tab switcher — owner / lead-with-permission only */}
          {canArchiveTaskLists && (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
              {[['active', '✓ Active'], ['archived', '🗃️ Archived']].map(([tab, label]) => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1, padding: '4px 6px', borderRadius: '6px', fontSize: '10px', fontWeight: 600, cursor: 'pointer',
                    border: activeTab === tab ? '1.5px solid #1a73e8' : '1.5px solid #e2e8f0',
                    background: activeTab === tab ? '#eff6ff' : 'white',
                    color: activeTab === tab ? '#1a73e8' : '#94a3b8',
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Section header + "show all / hide all" */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              My Calendars
            </div>
            {properties.length > 0 && (
              <button
                onClick={() => setHiddenProperties(allVisible ? new Set(properties) : new Set())}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#1a73e8', fontWeight: 600, padding: 0 }}>
                {allVisible ? 'Hide all' : 'Show all'}
              </button>
            )}
          </div>

          {/* Create Calendar — only in active tab */}
          {canManageCalendars && effectiveTab === 'active' && (
            <button
              onClick={() => setShowCreateCalModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#1a73e8', fontWeight: 600, padding: '2px 0', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', lineHeight: 1 }}>+</span> Create Calendar
            </button>
          )}

          {/* Empty state for archived tab */}
          {properties.length === 0 && effectiveTab === 'archived' && (
            <div style={{ textAlign: 'center', padding: '20px 0 8px', color: '#94a3b8', fontSize: '12px' }}>
              <div style={{ fontSize: '28px', marginBottom: '6px' }}>🗃️</div>
              No archived calendars.
            </div>
          )}

          {properties.map(addr => {
            const color = propertyColors[addr];
            const hidden = hiddenProperties.has(addr);
            const propEvs = allEvents.filter(e => e.record?.property_address === addr);
            const total = propEvs.length;
            const done = propEvs.filter(e => e.status === 'completed').length;
            const overdue = propEvs.filter(e => e.status !== 'completed' && e.date && e.date < now).length;
            const propRecord = visibleRecords.find(r => r.property_address === addr);
            const unpubInfo = unpublishedChanges[propRecord?.task_list_title];
            const unpubCount = unpubInfo?.count || 0;
            const isPublishing = publishingTitle === propRecord?.task_list_title;
            const isDeleting = deletingTitle === propRecord?.task_list_title;
            const isArchiving = archivingTitle === propRecord?.task_list_title;
            const isRestoring = restoringTitle === propRecord?.task_list_title;
            const isArchived = effectiveTab === 'archived';

            return (
              <div key={addr}
                style={{ borderRadius: '6px', padding: '6px 4px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div onClick={() => !isArchived && toggleProperty(addr)} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: isArchived ? 'default' : 'pointer' }}>
                  {/* Color checkbox */}
                  <div style={{ width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0, background: hidden || isArchived ? 'transparent' : color, border: `2px solid ${isArchived ? '#cbd5e1' : color}`, transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {!hidden && !isArchived && <span style={{ color: '#fff', fontSize: '10px', fontWeight: 900, lineHeight: 1 }}>✓</span>}
                  </div>
                  {/* Task list title */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', color: isArchived ? '#94a3b8' : hidden ? '#94a3b8' : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: hidden && !isArchived ? 'line-through' : 'none' }}>
                      {propRecord?.task_list_title || addr}
                    </div>
                    {/* Compact stat pills — only for non-archived */}
                    {!hidden && !isArchived && (
                      <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '10px', color: '#64748b' }}>{total} event{total !== 1 ? 's' : ''}</span>
                        {done > 0 && <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: 600 }}>· {done} done</span>}
                        {overdue > 0 && <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: 600 }}>· {overdue} overdue</span>}
                      </div>
                    )}
                  </div>
                </div>
                {/* Unpublished badge + Publish button — active tab only */}
                {!isArchived && unpubCount > 0 && propRecord && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', paddingLeft: '26px' }}>
                    <button
                      onClick={e => { e.stopPropagation(); handleFlashUnpublished(propRecord.task_list_title); }}
                      aria-label="Show unshared changes"
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(251,191,36,0.3)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                      onMouseDown={e => { e.currentTarget.style.transform = 'translateY(1px)'; e.currentTarget.style.boxShadow = ''; }}
                      onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(251,191,36,0.3)'; }}
                      style={{ fontSize: '10px', color: '#fbbf24', fontWeight: 600, background: 'none', border: 'none', padding: '2px 4px', borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit', transition: 'transform 0.12s ease, box-shadow 0.12s ease' }}
                    >
                      {unpubCount === 1 ? '1 unshared change' : `${unpubCount} unshared changes`}
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); openPublishModal(propRecord); }}
                      disabled={isPublishing}
                      style={{ padding: '2px 9px', borderRadius: '6px', border: 'none', fontSize: '10px', fontWeight: 700, cursor: isPublishing ? 'not-allowed' : 'pointer', background: isPublishing ? '#e2e8f0' : 'linear-gradient(135deg, #22c55e, #16a34a)', color: isPublishing ? '#94a3b8' : '#fff' }}>
                      {isPublishing ? '⏳…' : 'Share'}
                    </button>
                  </div>
                )}
                {/* Add Event — active tab, calendar managers only */}
                {!isArchived && canManageCalendars && propRecord && (
                  <div style={{ marginTop: '6px', paddingLeft: '26px' }}>
                    <button
                      onClick={e => { e.stopPropagation(); handleAddEvent(propRecord, currentDate); }}
                      style={{ padding: '3px 6px', borderRadius: '5px', border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}>
                      + Add Event
                    </button>
                  </div>
                )}
                {/* Archive + Delete — active tab, same row */}
                {!isArchived && (canArchiveTaskLists || canDeleteTaskLists) && propRecord && (
                  <div style={{ display: 'flex', gap: '5px', marginTop: '4px', paddingLeft: '26px' }}>
                    {canArchiveTaskLists && (
                      <button
                        onClick={e => { e.stopPropagation(); handleArchive(propRecord); }}
                        disabled={isArchiving}
                        style={{ padding: '3px 8px', borderRadius: '5px', border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: '10px', fontWeight: 600, cursor: isArchiving ? 'not-allowed' : 'pointer', opacity: isArchiving ? 0.6 : 1 }}>
                        {isArchiving ? '⏳ Archiving…' : '🗃️ Archive'}
                      </button>
                    )}
                    {canDeleteTaskLists && (
                      <button
                        onClick={e => { e.stopPropagation(); setConfirmDeleteRecord(propRecord); }}
                        disabled={isDeleting}
                        style={{ padding: '3px 7px', borderRadius: '5px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', fontSize: '10px', fontWeight: 600, cursor: isDeleting ? 'not-allowed' : 'pointer', opacity: isDeleting ? 0.6 : 1 }}>
                        {isDeleting ? '…' : 'Delete'}
                      </button>
                    )}
                  </div>
                )}
                {/* Unarchive + Delete — archived tab */}
                {isArchived && (canArchiveTaskLists || canDeleteTaskLists) && propRecord && (
                  <div style={{ display: 'flex', gap: '5px', marginTop: '4px', paddingLeft: '26px' }}>
                    {canArchiveTaskLists && (
                      <button
                        onClick={e => { e.stopPropagation(); handleRestore(propRecord); }}
                        disabled={isRestoring}
                        style={{ padding: '3px 8px', borderRadius: '5px', border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontSize: '10px', fontWeight: 600, cursor: isRestoring ? 'not-allowed' : 'pointer', opacity: isRestoring ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                        {isRestoring ? '⏳ Unarchiving…' : '↩️ Unarchive'}
                      </button>
                    )}
                    {canDeleteTaskLists && (
                      <button
                        onClick={e => { e.stopPropagation(); setConfirmDeleteRecord(propRecord); }}
                        disabled={isDeleting}
                        style={{ padding: '3px 7px', borderRadius: '5px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', fontSize: '10px', fontWeight: 600, cursor: isDeleting ? 'not-allowed' : 'pointer', opacity: isDeleting ? 0.6 : 1 }}>
                        {isDeleting ? '…' : 'Delete'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // ── Calendar event modal (create + edit) ─────────────────────────────────
  const renderEventModal = () => {
    if (!editingTask) return null;
    const et = editingTask.task;
    const isNew = !!editingTask.isNewEvent;
    const propColor = propertyColors[editingTask.record?.property_address] || '#1a73e8';
    const statuses = [
      { key: 'completed',  label: 'Done',        color: '#22c55e', bg: '#f0fdf4' },
      { key: 'inProgress', label: 'In Progress', color: '#3b82f6', bg: '#eff6ff' },
      { key: 'notStarted', label: 'Pending',     color: '#94a3b8', bg: '#f8fafc' },
    ];
    const isDirty = isNew
      ? (et.title?.trim().length > 0)
      : JSON.stringify(et) !== JSON.stringify(editingTask.originalTask);

    return (
      <>
        <div onClick={() => setEditingTask(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, backdropFilter: 'blur(2px)' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001, width: '480px', maxWidth: '96vw', maxHeight: '90vh', background: 'white', borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: `2px solid ${propColor}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ background: propColor, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff', marginBottom: '2px' }}>
                {isNew ? '+ New Event' : 'Edit Event'}
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>
                {editingTask.record.task_list_title}
              </div>
            </div>
            <button onClick={() => setEditingTask(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: '20px', lineHeight: 1, padding: '0 2px', marginTop: '-2px' }}>×</button>
          </div>

          {/* Body */}
          <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto' }}>

            {/* Item type segmented control */}
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px' }}>
              {[{ key: 'deadline', label: 'Deadline' }, { key: 'scheduled_event', label: 'Scheduled Event' }].map(({ key, label }) => {
                const active = (et.item_type || 'deadline') === key;
                return (
                  <button key={key}
                    onClick={() => setEditingTask(p => ({ ...p, task: { ...p.task, item_type: key } }))}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: 'none', background: active ? 'white' : 'transparent', color: active ? propColor : '#94a3b8', fontWeight: active ? 700 : 500, fontSize: '12px', cursor: 'pointer', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none', transition: 'all 0.15s' }}>
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Helper text */}
            <div style={{ fontSize: '12px', color: '#64748b', background: '#f8fafc', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', lineHeight: '1.5' }}>
              {(et.item_type || 'deadline') === 'deadline'
                ? 'Use this for contract deadlines, due dates, and closing milestones.'
                : 'Use this for appointments, meetings, inspections, showings, and scheduled calls.'}
            </div>

            {/* Title */}
            <input
              autoFocus={isNew}
              placeholder="Event title"
              value={et.title}
              onChange={e => setEditingTask(p => ({ ...p, task: { ...p.task, title: e.target.value } }))}
              style={{ fontWeight: 700, fontSize: '16px', color: '#1e293b', border: 'none', borderBottom: `2px solid ${propColor}`, outline: 'none', background: 'transparent', width: '100%', fontFamily: 'inherit', padding: '4px 0', lineHeight: '1.5' }}
            />

            {/* All-day toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
              <div style={{ position: 'relative', width: '36px', height: '20px', flexShrink: 0 }}>
                <input type="checkbox" checked={!!et.all_day}
                  onChange={e => setEditingTask(p => ({ ...p, task: { ...p.task, all_day: e.target.checked } }))}
                  style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                <div style={{ position: 'absolute', inset: 0, borderRadius: '10px', background: et.all_day ? propColor : '#e2e8f0', transition: 'background 0.2s' }} />
                <div style={{ position: 'absolute', top: '2px', left: et.all_day ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>All day</span>
            </label>

            {/* Deadline mode: Due Date */}
            {(et.item_type || 'deadline') === 'deadline' && (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Due Date</div>
                {et.all_day
                  ? <input type="date" value={toDateLocal(et.due || et.start)}
                      onClick={e => { try { e.target.showPicker(); } catch {} }}
                      onChange={e => { const iso = e.target.value ? new Date(e.target.value + 'T00:00:00').toISOString() : null; setEditingTask(p => ({ ...p, task: { ...p.task, due: iso, start: iso } })); }}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                  : <input type="datetime-local" value={toDatetimeLocal(et.due || et.start)}
                      onClick={e => { try { e.target.showPicker(); } catch {} }}
                      onChange={e => { const iso = e.target.value ? new Date(e.target.value).toISOString() : null; setEditingTask(p => ({ ...p, task: { ...p.task, due: iso, start: iso } })); }}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                }
              </div>
            )}

            {/* Scheduled Event mode: Start + End + Location */}
            {et.item_type === 'scheduled_event' && (
              <>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Start</div>
                  {et.all_day
                    ? <input type="date" value={toDateLocal(et.start || et.due)}
                        onClick={e => { try { e.target.showPicker(); } catch {} }}
                        onChange={e => { const iso = e.target.value ? new Date(e.target.value + 'T00:00:00').toISOString() : null; setEditingTask(p => ({ ...p, task: { ...p.task, start: iso, due: iso } })); }}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                    : <input type="datetime-local" value={toDatetimeLocal(et.start || et.due)}
                        onClick={e => { try { e.target.showPicker(); } catch {} }}
                        onChange={e => { const iso = e.target.value ? new Date(e.target.value).toISOString() : null; setEditingTask(p => ({ ...p, task: { ...p.task, start: iso, due: iso } })); }}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                  }
                </div>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>End <span style={{ color: '#cbd5e1', textTransform: 'none', fontWeight: 400, fontSize: '10px' }}>optional</span></div>
                  {et.all_day
                    ? <input type="date" value={toDateLocal(et.end)}
                        onClick={e => { try { e.target.showPicker(); } catch {} }}
                        onChange={e => setEditingTask(p => ({ ...p, task: { ...p.task, end: e.target.value ? new Date(e.target.value + 'T00:00:00').toISOString() : null } }))}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                    : <input type="datetime-local" value={toDatetimeLocal(et.end)}
                        onClick={e => { try { e.target.showPicker(); } catch {} }}
                        onChange={e => setEditingTask(p => ({ ...p, task: { ...p.task, end: e.target.value ? new Date(e.target.value).toISOString() : null } }))}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }} />
                  }
                </div>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Location <span style={{ color: '#cbd5e1', textTransform: 'none', fontWeight: 400, fontSize: '10px' }}>optional</span></div>
                  <input placeholder="Add location" value={et.location || ''}
                    onChange={e => setEditingTask(p => ({ ...p, task: { ...p.task, location: e.target.value } }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '13px', color: '#1e293b', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                </div>
              </>
            )}

            {/* Action Items (both modes) */}
            {(() => {
              const isCompleted = et.status === 'completed';
              const isInProgress = et.status === 'inProgress';
              const aiColor  = isCompleted ? '#22c55e' : isInProgress ? '#3b82f6' : '#94a3b8';
              const aiBorder = isCompleted ? '#bbf7d0' : isInProgress ? '#bfdbfe' : '#e2e8f0';
              return (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Action Items</div>
                  <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: `1px solid ${aiBorder}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(et.action_items || []).map((item, i) => {
                      const isExpanded = editTaskExpandedItems.has(i);
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '14px 1fr 22px', gap: '6px', alignItems: isExpanded ? 'flex-start' : 'center' }}>
                          <span
                            onClick={() => setEditTaskExpandedItems(prev => {
                              const next = new Set(prev);
                              isExpanded ? next.delete(i) : next.add(i);
                              return next;
                            })}
                            style={{ color: aiColor, fontSize: '12px', fontWeight: 700, lineHeight: '1.5', textAlign: 'center', cursor: 'pointer', userSelect: 'none', marginTop: isExpanded ? '2px' : 0 }}
                          >{isExpanded ? '⌄' : '›'}</span>
                          {isExpanded ? (
                            <textarea
                              ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                              value={item}
                              rows={1}
                              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                              onChange={e => setEditingTask(p => {
                                const items = [...(p.task.action_items || [])];
                                items[i] = e.target.value;
                                return { ...p, task: { ...p.task, action_items: items } };
                              })}
                              style={{ fontSize: '12px', color: '#1e293b', border: 'none', borderBottom: `1px solid ${aiBorder}`, background: 'transparent', outline: 'none', padding: '1px 2px', width: '100%', fontFamily: 'inherit', resize: 'none', overflow: 'hidden', lineHeight: '1.5', display: 'block' }}
                            />
                          ) : (
                            <span
                              onClick={() => setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(i); return next; })}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                              style={{ fontSize: '12px', color: item ? '#1e293b' : '#cbd5e1', fontStyle: item ? 'normal' : 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', borderRadius: '3px', padding: '1px 2px', margin: '0 -2px' }}
                            >{item || 'click to edit'}</span>
                          )}
                          <button
                            onClick={() => {
                              setEditingTask(p => ({ ...p, task: { ...p.task, action_items: (p.task.action_items || []).filter((_, idx) => idx !== i) } }));
                              setEditTaskExpandedItems(prev => {
                                const next = new Set();
                                for (const idx of prev) {
                                  if (idx < i) next.add(idx);
                                  else if (idx > i) next.add(idx - 1);
                                }
                                return next;
                              });
                            }}
                            title="Remove"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => {
                        const newIdx = (et.action_items || []).length;
                        setEditingTask(p => ({ ...p, task: { ...p.task, action_items: [...(p.task.action_items || []), ''] } }));
                        setEditTaskExpandedItems(prev => { const next = new Set(prev); next.add(newIdx); return next; });
                      }}
                      style={{ marginTop: '4px', padding: '4px 10px', borderRadius: '6px', border: `1.5px dashed ${aiColor}`, background: 'transparent', color: aiColor, fontSize: '11px', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', letterSpacing: '0.3px' }}>
                      + Add Item
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Status */}
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>Status</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {statuses.map(s => {
                  const active = et.status === s.key || (!et.status && s.key === 'notStarted');
                  return (
                    <button key={s.key}
                      onClick={() => setEditingTask(p => ({ ...p, task: { ...p.task, status: s.key } }))}
                      style={{ flex: 1, padding: '7px 4px', borderRadius: '8px', border: `2px solid ${active ? s.color : '#e2e8f0'}`, background: active ? s.bg : 'white', color: active ? s.color : '#94a3b8', fontWeight: active ? 700 : 500, fontSize: '12px', cursor: 'pointer', outline: 'none' }}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: isNew ? 'flex-end' : 'space-between', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc', flexShrink: 0 }}>
            {!isNew && canManageCalendars && (
              <button
                onClick={() => { setConfirmRemoveEvent({ record: editingTask.record, taskIdx: editingTask.taskIdx, eventTitle: et.title }); }}
                style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: 'rgba(239,68,68,0.08)', color: '#ef4444', fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>
                Remove Event
              </button>
            )}
            <div style={{ display: 'flex', gap: '10px', marginLeft: isNew ? 0 : 'auto' }}>
              <button onClick={() => setEditingTask(null)}
                style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={!isDirty}
                style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', background: isDirty ? `linear-gradient(135deg, ${propColor}, ${propColor}cc)` : '#e2e8f0', color: isDirty ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px', cursor: isDirty ? 'pointer' : 'not-allowed', boxShadow: isDirty ? `0 3px 10px ${propColor}66` : 'none' }}>
                {isNew ? 'Add Event' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', fontFamily: 'Inter, Segoe UI, Helvetica Neue, Arial, sans-serif' }}>
      <NavBar
        onSignInClick={() => setShowAuthModal(true)}
        onProfileClick={onProfileClick}
        onBillingClick={onBillingClick}
        onSeatsUpdate={onSeatsUpdate}
      />
      <AuthModal showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal} />

      <header className="App-header" style={{
        background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
        borderBottom: '1px solid #c8d8d2',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
        borderRadius: 0,
        margin: 0,
        padding: '0 20px',
        minHeight: 42,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', position: 'relative' }}>
          {user && user.accountType && (
            <div style={{
              position: 'absolute', left: 0,
              background: '#dcfce7', color: '#15803d',
              padding: '3px 10px', borderRadius: 999,
              fontSize: 12, fontWeight: 600,
              border: '1px solid #bbf7d0',
              letterSpacing: '0.01em',
            }}>
              {user.accountType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: '#d1fae5', color: '#047857',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
            }}>📅</div>
            <h2 style={{
              margin: 0, color: '#1e3a2f',
              fontSize: 18, fontWeight: 700,
              letterSpacing: '-0.01em',
            }}>Calendar</h2>
          </div>
        </div>
      </header>

      {loading && (
        <>
          <style>{`@keyframes cal-spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.85)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', border: '4px solid #e2e8f0', borderTop: '4px solid #1a73e8', borderRadius: '50%', animation: 'cal-spin 1s linear infinite' }} />
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#64748b' }}>Loading calendar…</span>
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0, background: '#fff' }}>
        {/* Sidebar */}
        <div style={{ width: '288px', flexShrink: 0, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflowY: 'auto', background: '#fafbfc' }}>
          {renderMiniCal()}

          {/* Overview stats */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>Overview</div>
            {[
              { label: 'Total tasks', count: stats.total,      color: '#64748b' },
              { label: 'Completed',   count: stats.completed,  color: '#22c55e' },
              { label: 'In progress', count: stats.inProgress, color: '#3b82f6' },
              { label: 'Overdue',     count: stats.overdue,    color: '#ef4444' },
            ].map(({ label, count, color }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '13px', color: '#475569' }}>{label}</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color }}>{count}</span>
              </div>
            ))}
          </div>

          {renderPropertyList()}
        </div>

        {/* Main area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, background: '#fff' }}>
            <button onClick={goToday}
              onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}
              style={{ padding: '7px 14px', borderRadius: '6px', border: '1px solid #dadce0', background: 'white', color: '#3c4043', fontWeight: 500, fontSize: '13px', cursor: 'pointer' }}>
              Today
            </button>
            <div style={{ display: 'flex' }}>
              {[['‹', goPrev], ['›', goNext]].map(([arrow, fn], i) => (
                <button key={i} onClick={fn}
                  onMouseEnter={e => e.currentTarget.style.background = '#f1f3f4'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  style={{ width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#3c4043', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                  {arrow}
                </button>
              ))}
            </div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 400, color: '#3c4043', flex: 1 }}>{periodLabel}</h2>
            <div style={{ display: 'flex', borderRadius: '6px', border: '1px solid #dadce0', overflow: 'hidden' }}>
              {[['month', 'Month'], ['week', 'Week'], ['day', 'Day']].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)}
                  style={{ padding: '7px 14px', border: 'none', background: view === v ? '#e8f0fe' : 'white', color: view === v ? '#1967d2' : '#3c4043', fontWeight: view === v ? 600 : 400, fontSize: '13px', cursor: 'pointer', borderRight: v !== 'day' ? '1px solid #dadce0' : 'none' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {view === 'month' && renderMonth()}
          {view === 'week' && renderWeek()}
          {view === 'day' && renderDay()}
        </div>
      </div>

      {renderEventModal()}

      {/* Publish recipient-selection modal */}
      {publishModalRecord && (() => {
        const { record, eligibleUsers } = publishModalRecord;
        const anySelected = publishSelectedEmails.size > 0;
        return (
          <>
            <div onClick={() => setPublishModalRecord(null)} style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1100, backdropFilter: 'blur(2px)'
            }} />
            <div style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1101, width: '400px', maxWidth: '94vw',
              backgroundColor: 'white', borderRadius: '14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              border: '2px solid #3b82f6',
              display: 'flex', flexDirection: 'column', overflow: 'hidden'
            }}>
              <div style={{ background: 'linear-gradient(145deg, #1a2540 0%, #243050 100%)', padding: '14px 18px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#fff', marginBottom: '2px' }}>📅 Share Task List</div>
                <div style={{ fontSize: '11px', color: '#a4afbe' }}>{record.task_list_title}</div>
              </div>
              <div style={{ padding: '18px 20px' }}>
                <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 14px 0' }}>
                  Selected people will receive SmartClose task updates. Calendar sync will run only for connected calendars.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {eligibleUsers.map(member => {
                    const email = member.email?.toLowerCase();
                    const isLockedInfo = !!member.lockedInfo;
                    const isSelected = !isLockedInfo && publishSelectedEmails.has(email);
                    const displayName = email?.split('@')[0] || email;
                    const isLead = member.role === 'team_lead';
                    const isSelf = email === user?.email?.toLowerCase();
                    const selfConnected = isSelf && selfCalendarStatus?.connected === true;
                    const providerLabel = selfCalendarStatus?.provider === 'google' ? 'Google'
                      : selfCalendarStatus?.provider === 'outlook' ? 'Outlook'
                      : selfCalendarStatus?.provider === 'icloud' ? 'iCloud'
                      : 'Calendar';
                    const isDisabled = isLockedInfo;

                    let rowBorder, rowBg, rowCursor;
                    if (isLockedInfo) {
                      rowBorder = '1.5px solid #bfdbfe'; rowBg = '#f0f7ff'; rowCursor = 'default';
                    } else if (selfConnected) {
                      rowBorder = `1.5px solid ${isSelected ? '#10b981' : '#d1fae5'}`; rowBg = isSelected ? '#f0fdf4' : '#fafffe'; rowCursor = 'pointer';
                    } else {
                      rowBorder = `1.5px solid ${isSelected ? '#3b82f6' : '#e2e8f0'}`; rowBg = isSelected ? '#eff6ff' : 'white'; rowCursor = 'pointer';
                    }

                    return (
                      <label key={email} style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 12px', borderRadius: '8px',
                        border: rowBorder, background: rowBg, cursor: rowCursor,
                        transition: 'all 0.15s ease'
                      }}>
                        <input
                          type="checkbox"
                          checked={isLockedInfo ? true : isSelected}
                          disabled={isDisabled}
                          onChange={isDisabled ? undefined : () => setPublishSelectedEmails(prev => {
                            const next = new Set(prev);
                            isSelected ? next.delete(email) : next.add(email);
                            return next;
                          })}
                          style={{ width: '16px', height: '16px', accentColor: isLockedInfo ? '#93c5fd' : selfConnected ? '#10b981' : '#3b82f6', cursor: isDisabled ? 'default' : 'pointer' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                            {isSelf ? 'You' : displayName}
                          </div>
                          {isSelf ? (
                            selfConnected
                              ? <div style={{ fontSize: '11px', color: '#059669' }}>{providerLabel} Calendar connected</div>
                              : <div style={{ fontSize: '11px', color: '#94a3b8' }}>No calendar connected · SmartClose update only</div>
                          ) : (
                            <div style={{ fontSize: '11px', color: '#64748b', wordBreak: 'break-all' }}>{email}</div>
                          )}
                        </div>
                        {isSelf && (
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', whiteSpace: 'nowrap',
                            background: selfConnected ? '#d1fae5' : '#f1f5f9',
                            color: selfConnected ? '#065f46' : '#94a3b8' }}>
                            You
                          </span>
                        )}
                        {!isSelf && isLead && !isLockedInfo && (
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#dbeafe', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                            Lead Agent
                          </span>
                        )}
                        {isLockedInfo && (
                          <>
                            {isLead && (
                              <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#dbeafe', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                                Lead Agent
                              </span>
                            )}
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#e0f2fe', color: '#0369a1', whiteSpace: 'nowrap' }}>
                              Will receive
                            </span>
                          </>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #f1f5f9', background: '#fafbfc' }}>
                <button onClick={() => setPublishModalRecord(null)} style={{
                  padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                  background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer'
                }}>Cancel</button>
                <button onClick={handlePublishConfirm} disabled={!anySelected} style={{
                  padding: '8px 24px', borderRadius: '8px', border: 'none',
                  background: anySelected ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e2e8f0',
                  color: anySelected ? 'white' : '#94a3b8', fontWeight: 700, fontSize: '13px',
                  cursor: anySelected ? 'pointer' : 'not-allowed',
                  boxShadow: anySelected ? '0 3px 10px rgba(59,130,246,0.4)' : 'none'
                }}>Share</button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Unshared-changes navigation warning */}
      {pendingNavUrl && (
        <>
          <div onClick={() => setPendingNavUrl(null)} style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1200, backdropFilter: 'blur(2px)'
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1201, width: '440px', maxWidth: '94vw',
            backgroundColor: 'white', borderRadius: '14px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{ height: '4px', background: '#f59e0b' }} />
            <div style={{ padding: '24px 28px 24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>
                Unshared Calendar Changes
              </h3>
              <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: '0 0 22px' }}>
                You have unshared calendar changes. Your changes are saved for you, but they have not been shared with the team or calendars yet.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setPendingNavUrl(null)}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                >Cancel</button>
                <button
                  onClick={() => { const url = pendingNavUrl; setPendingNavUrl(null); navigate(url); }}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                >Leave Without Sharing</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Create Calendar modal ──────────────────────────────────────── */}
      {showCreateCalModal && (
        <>
          <div onClick={() => { setShowCreateCalModal(false); setCreateCalError(''); }}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(2px)' }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 1101, width: '420px', maxWidth: '94vw',
            backgroundColor: 'white', borderRadius: '14px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: '2px solid #3b82f6',
            overflow: 'hidden',
          }}>
            <div style={{ height: '4px', background: 'linear-gradient(90deg,#3b82f6,#1d4ed8)' }} />
            <div style={{ padding: '24px 28px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: '0 0 18px' }}>Create New Calendar</h3>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>Calendar Name *</label>
                <input
                  autoFocus
                  value={createCalTitle}
                  onChange={e => setCreateCalTitle(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateCal(); if (e.key === 'Escape') { setShowCreateCalModal(false); setCreateCalError(''); } }}
                  placeholder="e.g. 123 Main St Schedule"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>Property Address (optional)</label>
                <input
                  value={createCalAddress}
                  onChange={e => setCreateCalAddress(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateCal(); if (e.key === 'Escape') { setShowCreateCalModal(false); setCreateCalError(''); } }}
                  placeholder="e.g. 123 Main St, City, State"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
              {createCalError && (
                <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '14px', padding: '8px 12px', background: '#fef2f2', borderRadius: '6px', border: '1px solid #fca5a5' }}>
                  {createCalError}
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowCreateCalModal(false); setCreateCalError(''); }}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button
                  onClick={handleCreateCal}
                  disabled={isCreatingCal || !createCalTitle.trim()}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: isCreatingCal || !createCalTitle.trim() ? '#e2e8f0' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: isCreatingCal || !createCalTitle.trim() ? '#94a3b8' : 'white', fontWeight: 700, fontSize: '13px', cursor: isCreatingCal || !createCalTitle.trim() ? 'not-allowed' : 'pointer' }}>
                  {isCreatingCal ? 'Creating…' : 'Create Calendar'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Delete Calendar confirmation ───────────────────────────────── */}
      {confirmDeleteRecord && (
        <>
          <div onClick={() => setConfirmDeleteRecord(null)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(2px)' }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 1101, width: '400px', maxWidth: '94vw',
            backgroundColor: 'white', borderRadius: '14px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: '2px solid #ef4444',
            overflow: 'hidden',
          }}>
            <div style={{ height: '4px', background: 'linear-gradient(90deg,#ef4444,#dc2626)' }} />
            <div style={{ padding: '24px 28px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>Delete Calendar</h3>
              <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: '0 0 6px' }}>
                Are you sure you want to delete <strong>"{confirmDeleteRecord.task_list_title}"</strong>?
              </p>
              <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 22px' }}>
                This will remove the calendar list and all its events. This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setConfirmDeleteRecord(null)}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteCalendar}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                  Delete Calendar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Remove Event confirmation ──────────────────────────────────── */}
      {confirmRemoveEvent && (
        <>
          <div onClick={() => setConfirmRemoveEvent(null)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(2px)' }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 1101, width: '380px', maxWidth: '94vw',
            backgroundColor: 'white', borderRadius: '14px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: '2px solid #f87171',
            overflow: 'hidden',
          }}>
            <div style={{ height: '4px', background: 'linear-gradient(90deg,#f87171,#ef4444)' }} />
            <div style={{ padding: '24px 28px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>Remove Event</h3>
              <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: '0 0 22px' }}>
                Remove <strong>"{confirmRemoveEvent.eventTitle || 'this event'}"</strong> from the calendar?
                This change will be saved as a draft until you publish.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setConfirmRemoveEvent(null)}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRemoveEvent}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#f87171,#ef4444)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                  Remove Event
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Archive Calendar confirmation ──────────────────────────────── */}
      {confirmArchiveRecord && (
        <>
          <div onClick={() => setConfirmArchiveRecord(null)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100, backdropFilter: 'blur(2px)' }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 1101, width: '400px', maxWidth: '94vw',
            backgroundColor: 'white', borderRadius: '14px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: '2px solid #6366f1',
            overflow: 'hidden',
          }}>
            <div style={{ height: '4px', background: 'linear-gradient(90deg,#6366f1,#4f46e5)' }} />
            <div style={{ padding: '24px 28px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>Archive Calendar?</h3>
              <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: '0 0 6px' }}>
                <strong>"{confirmArchiveRecord.task_list_title}"</strong> will move to Archived.
              </p>
              <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 22px' }}>
                The calendar will be hidden from the Active view and remote calendar resources will be removed for all team members. You can restore it at any time from the Archived tab.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setConfirmArchiveRecord(null)}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button
                  onClick={handleConfirmArchive}
                  style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                  Archive
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Toast notification ─────────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', color: '#fff', padding: '10px 20px', borderRadius: '8px',
          fontSize: '13px', fontWeight: 600, zIndex: 2000,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
